import { useState, useEffect, useCallback } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  LogIn, User, Users, Plus, Trash2, Hotel, Phone, CreditCard, 
  Calendar, Loader2, AlertTriangle, Bed, Info, CheckCircle2, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { useTabs } from '@/context/TabsContext';
import IdDocumentUpload from '@/components/IdDocumentUpload';
import PhoneInput from '@/components/PhoneInput';
import AddressInput from '@/components/AddressInput';
import { useResourceLock } from '@/hooks/useResourceLock';

export default function CheckinTab({ tabId, data }) {
  const { closeTab } = useTabs();
  const { bookingId, onComplete } = data || {};
  
  // Acquire lock on mount
  const { hasLock, lockedBy, acquireLock, releaseLock, loading: lockLoading } = 
    useResourceLock('booking', bookingId, 'checkin', true);
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [details, setDetails] = useState(null);
  const [idTypes, setIdTypes] = useState([]);
  const [searchingPhone, setSearchingPhone] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  
  const [formData, setFormData] = useState({
    check_in: '',
    check_out: '',
    notes: '',
    room_id: '',
    advance_paid: '',
    discount: '',
    primary_guest: {
      phone: '',
      first_name: '',
      last_name: '',
      email: '',
      id_type: '',
      id_number: '',
      id_proof_image: '',
      address: {
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        pin_code: '',
        country: 'India'
      }
    },
    existing_guest_id: null,
    additional_guests: []
  });

  useEffect(() => {
    if (bookingId) {
      loadCheckInDetails();
    }
  }, [bookingId]);

  const loadCheckInDetails = async () => {
    setLoading(true);
    try {
      const [detailsRes, idTypesRes] = await Promise.all([
        api.get(`/bookings/${bookingId}/checkin-details`),
        api.get('/id-types')
      ]);
      
      const data = detailsRes.data;
      setDetails(data);
      setIdTypes(idTypesRes.data || []);
      
      if (!data.booking?.room_id) {
        loadAvailableRooms();
      }
      
      const checkIn = data.booking?.check_in || data.booking?.check_in_date;
      const checkOut = data.booking?.check_out || data.booking?.check_out_date;
      
      let checkInFormatted = '';
      let checkOutFormatted = '';
      
      try {
        if (checkIn) {
          const d = new Date(checkIn);
          if (!isNaN(d.getTime())) checkInFormatted = d.toISOString().slice(0, 10);
        }
        if (checkOut) {
          const d = new Date(checkOut);
          if (!isNaN(d.getTime())) checkOutFormatted = d.toISOString().slice(0, 10);
        }
      } catch (e) {
        console.error('Error parsing dates:', e);
      }
      
      const pg = data.primary_guest;
      const nameParts = (pg?.name || '').split(' ');
      
      const additionalGuestIds = data.booking?.additional_guest_ids || [];
      const additionalGuestsData = additionalGuestIds.map(gid => {
        const ag = data.all_guests?.find(g => g.id === gid);
        const agNameParts = (ag?.name || '').split(' ');
        return {
          id: gid,
          first_name: ag?.first_name || agNameParts[0] || '',
          last_name: ag?.last_name || agNameParts.slice(1).join(' ') || '',
          phone: ag?.phone || '',
          id_type: ag?.id_type_name || '',
          id_number: ag?.id_number || '',
          id_proof_image: ag?.id_proof_image || ''
        };
      });
      
      setFormData({
        check_in: checkInFormatted,
        check_out: checkOutFormatted,
        notes: data.booking?.notes || '',
        advance_paid: data.booking?.advance_paid?.toString() || '0',
        discount: data.booking?.discount?.toString() || '0',
        room_id: data.booking?.room_id || '',
        primary_guest: {
          phone: pg?.phone || '',
          first_name: pg?.first_name || nameParts[0] || '',
          last_name: pg?.last_name || nameParts.slice(1).join(' ') || '',
          email: pg?.email || '',
          id_type: pg?.id_type_name || '',
          id_number: pg?.id_number || '',
          id_proof_image: pg?.id_proof_image || '',
          address: pg?.address && typeof pg.address === 'object' 
            ? pg.address 
            : { address_line1: '', address_line2: '', city: '', state: '', pin_code: '', country: 'India' }
        },
        existing_guest_id: pg?.id || null,
        additional_guests: additionalGuestsData
      });
    } catch (error) {
      toast.error('Failed to load check-in details');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableRooms = async () => {
    setLoadingRooms(true);
    try {
      const res = await api.get(`/rooms/available-for-checkin/${bookingId}`);
      const { matching_rooms, all_available_rooms } = res.data;
      setAvailableRooms(matching_rooms.length > 0 ? matching_rooms : all_available_rooms);
      
      if (matching_rooms.length === 1) {
        setFormData(prev => ({ ...prev, room_id: matching_rooms[0].id }));
      }
    } catch (error) {
      console.error('Failed to load available rooms:', error);
    } finally {
      setLoadingRooms(false);
    }
  };

  const searchGuestByPhone = useCallback(async (phone) => {
    if (!phone || phone.length < 10) return;
    
    setSearchingPhone(true);
    try {
      const res = await api.get(`/guests/search/phone/${encodeURIComponent(phone)}`);
      if (res.data.found && res.data.guest) {
        const guest = res.data.guest;
        const nameParts = (guest.name || '').split(' ');
        
        // Ensure address is an object
        const guestAddress = guest.address && typeof guest.address === 'object' 
          ? guest.address 
          : { address_line1: '', address_line2: '', city: '', state: '', pin_code: '', country: 'India' };
        
        setFormData(prev => ({
          ...prev,
          existing_guest_id: guest.id,
          primary_guest: {
            ...prev.primary_guest,
            phone: guest.phone || phone,
            first_name: guest.first_name || nameParts[0] || '',
            last_name: guest.last_name || nameParts.slice(1).join(' ') || '',
            email: guest.email || '',
            id_type: guest.id_type_name || '',
            id_number: guest.id_number || '',
            id_proof_image: guest.id_proof_image || '',
            address: guestAddress
          }
        }));
        toast.success('Guest found! Details auto-filled.');
      }
    } catch (error) {
      // Not found is OK
    } finally {
      setSearchingPhone(false);
    }
  }, []);

  const updatePrimaryGuest = (field, value) => {
    setFormData(prev => ({
      ...prev,
      primary_guest: { ...prev.primary_guest, [field]: value },
      existing_guest_id: field === 'phone' ? null : prev.existing_guest_id
    }));
  };

  const addAdditionalGuest = () => {
    const roomCapacity = details?.room_capacity || 2;
    if (1 + formData.additional_guests.length >= roomCapacity) {
      toast.error(`Maximum ${roomCapacity} guests allowed for this room`);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      additional_guests: [...prev.additional_guests, {
        first_name: '',
        last_name: '',
        phone: '',
        id_type: '',
        id_number: '',
        id_proof_image: ''
      }]
    }));
  };

  const removeAdditionalGuest = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_guests: prev.additional_guests.filter((_, i) => i !== index)
    }));
  };

  const updateAdditionalGuest = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      additional_guests: prev.additional_guests.map((ag, i) => 
        i === index ? { ...ag, [field]: value } : ag
      )
    }));
  };

  const getTotalGuests = () => 1 + formData.additional_guests.length;
  const canCheckIn = () => getTotalGuests() <= (details?.room_capacity || 2);

  // Calculate summary
  const calculateSummary = () => {
    let nights = details?.total_nights || details?.booking?.total_nights;
    if (!nights) {
      // Calculate from dates
      const checkIn = new Date(details?.booking?.check_in || details?.booking?.check_in_date || formData.check_in);
      const checkOut = new Date(details?.booking?.check_out || details?.booking?.check_out_date || formData.check_out);
      nights = Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));
      if (isNaN(nights) || nights < 1) nights = 1;
    }
    const rate = details?.booking?.room_rate || 0;
    const subtotal = nights * rate;
    const discount = parseFloat(formData.discount || 0);
    const taxRate = details?.tax_rate || 12;
    const tax = (subtotal - discount) * (taxRate / 100);
    const total = subtotal - discount + tax;
    const advance = parseFloat(formData.advance_paid || 0);
    return { nights, rate, subtotal, discount, tax, total, advance, balance: total - advance, taxRate };
  };

  const handleCheckIn = async () => {
    if (!details?.booking?.room_id && !formData.room_id) {
      toast.error('Please select a room for check-in');
      return;
    }
    
    if (!formData.primary_guest.phone) {
      toast.error('Primary guest phone number is required');
      return;
    }
    if (!formData.primary_guest.first_name) {
      toast.error('Primary guest first name is required');
      return;
    }
    if (!formData.primary_guest.id_type || !formData.primary_guest.id_number) {
      toast.error('Primary guest ID document is mandatory for check-in');
      return;
    }
    
    // Validate address - handle both string and object formats
    const address = formData.primary_guest.address;
    const hasValidAddress = address && (
      typeof address === 'string' 
        ? address.trim() !== '' 
        : (address.address_line1 && address.address_line1.trim() !== '')
    );
    
    if (!hasValidAddress) {
      toast.error('Primary guest address is mandatory for check-in');
      return;
    }

    if (!canCheckIn()) {
      toast.error(`Cannot check in ${getTotalGuests()} guests. Room capacity is ${details?.room_capacity}.`);
      return;
    }

    setProcessing(true);
    try {
      const checkinData = {
        check_in: formData.check_in ? new Date(formData.check_in).toISOString() : null,
        check_out: formData.check_out ? new Date(formData.check_out).toISOString() : null,
        notes: formData.notes,
        advance_paid: parseFloat(formData.advance_paid || 0),
        discount: parseFloat(formData.discount || 0),
        primary_guest: formData.primary_guest,
        additional_guests: formData.additional_guests.filter(ag => ag.first_name && ag.phone),
        room_id: formData.room_id || null
      };

      await api.patch(`/bookings/${bookingId}/checkin`, checkinData);
      toast.success('Check-in successful!');
      onComplete?.();
      closeTab(tabId);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Check-in failed'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        <span className="ml-2 text-gray-600">Loading check-in details...</span>
      </div>
    );
  }

  // Show lock warning if someone else has the lock
  if (!hasLock && lockedBy) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 max-w-md text-center">
          <Lock className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-amber-800 mb-2">Check-in In Progress</h2>
          <p className="text-amber-700 mb-4">
            This booking is currently being processed by <strong>{lockedBy}</strong>.
          </p>
          <p className="text-sm text-amber-600 mb-6">
            Please wait for them to complete or close the tab, or try again later.
          </p>
          <Button variant="outline" onClick={() => closeTab(tabId)}>
            Close Tab
          </Button>
        </div>
      </div>
    );
  }

  const getRoomDisplay = () => {
    if (details?.booking?.room_id) {
      return details?.room?.room_number || 'Unknown';
    }
    if (formData.room_id) {
      const selectedRoom = availableRooms.find(r => r.id === formData.room_id);
      return selectedRoom?.room_number || 'Selected';
    }
    return 'Not Assigned';
  };

  const summary = calculateSummary();

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <LogIn className="h-6 w-6 text-emerald-600" />
            Check-In Guest
          </h1>
          <p className="text-gray-500 mt-1">
            Room {getRoomDisplay()} • Booking #{bookingId?.substring(0, 8).toUpperCase()}
          </p>
        </div>
        <Badge className={`px-3 py-1 ${canCheckIn() ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
          {getTotalGuests()} / {details?.room_capacity || 2} Guests
        </Badge>
      </div>

      {/* Content */}
      <div className="flex-1 grid grid-cols-3 gap-6 overflow-hidden">
        {/* Left Column - Room & Dates */}
        <ScrollArea className="pr-4">
          <div className="space-y-4 pb-4">
            {/* Room Selection */}
            {!details?.booking?.room_id && (
              <Card className="border-amber-200">
                <CardHeader className="py-3 px-4 bg-amber-50 border-b">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-700">
                    <Bed className="h-4 w-4" />
                    Select Room (Required)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 mb-3">
                    {details?.booking?.room_type_name || 'Room'} • 
                    {details?.booking?.smoking_preference === 'smoking' ? ' Smoking' : 
                     details?.booking?.smoking_preference === 'non_smoking' ? ' Non-Smoking' : ' Any'}
                  </p>
                  {loadingRooms ? (
                    <div className="flex items-center gap-2 text-amber-600">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading rooms...
                    </div>
                  ) : availableRooms.length > 0 ? (
                    <Select 
                      value={formData.room_id} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, room_id: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select room" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRooms.map(room => (
                          <SelectItem key={room.id} value={room.id}>
                            Room {room.room_number} - Floor {room.floor || 1}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-red-600 text-sm flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      No rooms available
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Room Info (if assigned) */}
            {details?.booking?.room_id && details?.room && (
              <Card className="border-emerald-200">
                <CardHeader className="py-3 px-4 bg-emerald-50 border-b">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-emerald-700">
                    <Hotel className="h-4 w-4" />
                    Room Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">Room Number</p>
                      <p className="font-semibold">{details.room.room_number}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Floor</p>
                      <p className="font-semibold">{details.room.floor || 1}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Type</p>
                      <p className="font-semibold">{details.room.room_type_name || details.booking?.room_type_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Capacity</p>
                      <p className="font-semibold">{details.room_capacity || 2} guests</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Dates */}
            <Card className="border-blue-200">
              <CardHeader className="py-3 px-4 bg-blue-50 border-b">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-blue-700">
                  <Calendar className="h-4 w-4" />
                  Stay Dates
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Check-in</Label>
                    <Input
                      type="date"
                      value={formData.check_in}
                      onChange={(e) => setFormData(prev => ({ ...prev, check_in: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Check-out</Label>
                    <Input
                      type="date"
                      value={formData.check_out}
                      onChange={(e) => setFormData(prev => ({ ...prev, check_out: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment */}
            <Card>
              <CardHeader className="py-3 px-4 bg-gray-50 border-b">
                <CardTitle className="text-sm font-medium">Payment Details</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Advance Payment (₹)</Label>
                    <Input
                      type="number"
                      value={formData.advance_paid}
                      onChange={(e) => setFormData(prev => ({ ...prev, advance_paid: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Discount (₹)</Label>
                    <Input
                      type="number"
                      value={formData.discount}
                      onChange={(e) => setFormData(prev => ({ ...prev, discount: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>

        {/* Middle Column - Guest Details */}
        <ScrollArea className="pr-4">
          <div className="space-y-4 pb-4">
            {/* Primary Guest */}
            <Card className="border-cyan-200">
              <CardHeader className="py-3 px-4 bg-cyan-50 border-b">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-cyan-700">
                  <User className="h-4 w-4" />
                  Primary Guest
                  {formData.existing_guest_id && (
                    <Badge className="bg-green-100 text-green-700 ml-2 text-xs">Existing</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div>
                  <Label className="text-xs text-gray-500 flex items-center gap-1">
                    <Phone className="h-3 w-3" /> Phone * (Mandatory)
                  </Label>
                  <div className="relative mt-1">
                    <PhoneInput
                      value={formData.primary_guest.phone}
                      onChange={(value) => {
                        updatePrimaryGuest('phone', value);
                        if (value && value.length >= 10) {
                          searchGuestByPhone(value);
                        }
                      }}
                      required
                    />
                    {searchingPhone && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">First Name *</Label>
                    <Input
                      value={formData.primary_guest.first_name}
                      onChange={(e) => updatePrimaryGuest('first_name', e.target.value)}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Last Name</Label>
                    <Input
                      value={formData.primary_guest.last_name}
                      onChange={(e) => updatePrimaryGuest('last_name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-gray-500">Email</Label>
                  <Input
                    type="email"
                    value={formData.primary_guest.email}
                    onChange={(e) => updatePrimaryGuest('email', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs text-gray-500">Address * (Mandatory)</Label>
                  <AddressInput
                    value={formData.primary_guest.address}
                    onChange={(value) => updatePrimaryGuest('address', value)}
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">ID Type * (Mandatory)</Label>
                    <Select 
                      value={formData.primary_guest.id_type} 
                      onValueChange={(v) => updatePrimaryGuest('id_type', v)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select ID" />
                      </SelectTrigger>
                      <SelectContent>
                        {idTypes.length > 0 ? (
                          idTypes.map(type => (
                            <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="Aadhaar">Aadhaar</SelectItem>
                            <SelectItem value="Passport">Passport</SelectItem>
                            <SelectItem value="Driving License">Driving License</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">ID Number * (Mandatory)</Label>
                    <Input
                      value={formData.primary_guest.id_number}
                      onChange={(e) => updatePrimaryGuest('id_number', e.target.value)}
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                <IdDocumentUpload
                  value={formData.primary_guest.id_proof_image}
                  onChange={(url) => updatePrimaryGuest('id_proof_image', url)}
                  label="ID Document Photo"
                />
              </CardContent>
            </Card>

            {/* Additional Guests */}
            <Card>
              <CardHeader className="py-3 px-4 bg-gray-50 border-b flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Additional Guests ({formData.additional_guests.length})
                </CardTitle>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addAdditionalGuest}
                  className="h-7 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </CardHeader>
              <CardContent className="p-4">
                {formData.additional_guests.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No additional guests</p>
                ) : (
                  <div className="space-y-4">
                    {formData.additional_guests.map((guest, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg relative">
                        <button
                          onClick={() => removeAdditionalGuest(index)}
                          className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <p className="text-xs font-medium text-gray-600 mb-2">Guest {index + 2}</p>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            placeholder="First Name *"
                            value={guest.first_name}
                            onChange={(e) => updateAdditionalGuest(index, 'first_name', e.target.value)}
                            className="h-8 text-sm"
                          />
                          <Input
                            placeholder="Phone *"
                            value={guest.phone}
                            onChange={(e) => updateAdditionalGuest(index, 'phone', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </ScrollArea>

        {/* Right Column - Summary */}
        <div className="flex flex-col">
          <Card className="flex-1 flex flex-col">
            <CardHeader className="py-3 px-4 bg-gradient-to-r from-emerald-50 to-blue-50 border-b">
              <CardTitle className="text-sm font-medium">Check-In Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-4 flex-1 flex flex-col">
              <div className="space-y-3 flex-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Room Rate</span>
                  <span className="font-medium">₹{summary.rate}/night</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Nights</span>
                  <span className="font-medium">{summary.nights}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">₹{summary.subtotal}</span>
                </div>
                {summary.discount > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Discount</span>
                    <span>- ₹{summary.discount}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax ({summary.taxRate}%)</span>
                  <span className="font-medium">₹{summary.tax.toFixed(0)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between text-lg">
                  <span className="font-bold">Total</span>
                  <span className="font-bold text-emerald-600">₹{summary.total.toFixed(0)}</span>
                </div>
                {summary.advance > 0 && (
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>Advance Paid</span>
                    <span>- ₹{summary.advance}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Balance Due</span>
                  <span className={`font-bold ${summary.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    ₹{summary.balance.toFixed(0)}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2 pt-4 border-t mt-auto">
                <Button
                  onClick={handleCheckIn}
                  disabled={processing || !canCheckIn()}
                  className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white h-11"
                >
                  {processing ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                  ) : (
                    <><CheckCircle2 className="h-5 w-5 mr-2" /> Complete Check-In</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => closeTab(tabId)}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
